import type { Well } from '../types';

// Minimal county -> basin helper to enrich wells from compact payloads
function basinFromCounty(county: string): string {
  const normalized = (county || '').toLowerCase();
  if (normalized === 'eddy' || normalized === 'lea') return 'Delaware';
  if (normalized === 'san juan' || normalized === 'rio arriba' || normalized === 'mckinley' || normalized === 'sandoval') return 'San Juan';
  if (normalized === 'colfax') return 'Raton';
  if (normalized === 'chaves' || normalized === 'roosevelt' || normalized === 'lincoln' || normalized === 'curry' || normalized === 'de baca') return 'Permian';
  return county || 'Unknown';
}

// Simple parser for the facilities response
// Handles both compact (array) and regular (object) formats
// Browser fetch() automatically handles gzip decompression via Accept-Encoding
export async function parseCompactWellsStream(response: Response): Promise<Well[]> {
  if (!response.ok) {
    throw new Error(`Bad response: ${response.status}`);
  }

  console.log('[compactParser] Parsing response...');
  
  // Let the browser handle decompression automatically via response.json()
  // This works because fetch() negotiates Accept-Encoding: gzip with server
  let data: any;
  try {
    data = await response.json();
    console.log('[compactParser] JSON parsed successfully');
  } catch (err) {
    console.error('[compactParser] JSON parse failed:', err);
    throw new Error('Failed to parse JSON response');
  }
  
  // Extract fields and facilities
  const fields: string[] = data.fields || ['id', 'name', 'identifier', 'latitude', 'longitude', 'county', 'operator', 'status'];
  const facilities: any[] = data.facilities || [];
  
  console.log('[compactParser] Parsed', facilities.length, 'facilities');
  
  // Check if we have compact (array) or regular (object) format
  if (facilities.length > 0) {
    const firstItem = facilities[0];
    if (Array.isArray(firstItem)) {
      // Compact format: arrays
      console.log('[compactParser] Using compact array format with fields:', fields);
      return facilities.map(arr => compactArrayToWell(arr, fields));
    } else {
      // Regular format: objects
      console.log('[compactParser] Using regular object format');
      return facilities.map(obj => objectToWell(obj));
    }
  }
  
  return [];
}

function compactArrayToWell(arr: any[], fields: string[]): Well {
  const get = (key: string) => {
    const idx = fields.indexOf(key);
    return idx >= 0 ? arr[idx] : undefined;
  };

  const getAny = (...keys: string[]) => {
    for (const key of keys) {
      const value = get(key);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return undefined;
  };

  const county = (getAny('county', 'district') as string) || 'Unknown';
  const api = (get('identifier') as string) || (arr[0] as string) || '';
  const rawType = (get('type') as string) || 'well';
  const state = (get('state') as string) || 'NM';
  const facilityType = (getAny('facilityType?', 'facilityType') as string) || undefined;

  return {
    id: (get('id') as string) || api,
    api,
    wellName: (get('name') as string) || 'Unknown',
    operator: (getAny('operator', 'operatorName') as string) || 'Unknown',
    latitude: Number(getAny('latitude') ?? 0),
    longitude: Number(getAny('longitude') ?? 0),
    status: (get('status') as string) || 'Unknown',
    basin: basinFromCounty(county),
    county,
    state,
    type: (rawType || 'well').toLowerCase(),
    ...(facilityType ? { facilityType } : {})
  };
}

// Convert regular object format to Well
function objectToWell(obj: any): Well {
  const county = obj.county || obj.district || 'Unknown';
  const api = obj.identifier || obj.api || obj.apiNumber || '';
  
  return {
    id: obj.id || api || `well-${obj.latitude}-${obj.longitude}`,
    api,
    wellName: obj.name || obj.wellName || 'Unknown',
    operator: obj.operator || obj.operatorName || 'Unknown',
    latitude: Number(obj.latitude) || 0,
    longitude: Number(obj.longitude) || 0,
    status: obj.status || 'Unknown',
    basin: obj.basin || basinFromCounty(county),
    county,
    state: obj.state || 'NM',
    type: (obj.type || 'well').toLowerCase(),
    ...(obj.facilityType ? { facilityType: obj.facilityType } : {})
  };
}
